import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { displayQuarter } from '@/helpers';
import { usePermitStatus } from '../../../hooks';
import { generateLabel } from '@/features/projects/helpers';
import { Button, Icon, Typography } from '@/components';

type Permit = {
  type: string;
  title?: string | null;
  status?: string;
  date?: string | null;
};

type NormalizedProject = {
  permits?: Permit[];
  permitsComments?: string | null;
};

type PermitsProps = {
  readonly normalizedProject: NormalizedProject;
  readonly country?: string;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
};

export function PermitsBox({ normalizedProject, country, onEdit, isEditable }: PermitsProps) {
  const { t } = useTranslation(['projects']);
  const { map: mapStatus } = usePermitStatus();

  const normalizedpermits = normalizedProject.permits;
  const permits = normalizedpermits || [];
  if (!normalizedpermits && !isEditable) return null;

  const formatPermit = useCallback(
    (permit: { label: string; status?: string; date?: string | null }) => {
      const value = permit.status
        ? `${mapStatus(permit.status)}${permit.date ? ` (${displayQuarter(permit.date)})` : ''}`
        : null;

      return {
        label: permit.label,
        value,
      };
    },
    [mapStatus]
  );

  const { permitFields, comments } = useMemo(() => {
    const getPermit = (type: string) => permits.find((p) => p.type === type);

    const iva = getPermit('connection');
    const dia = getPermit('environmental');
    const aap = getPermit('previous');
    const aac = getPermit('construction');
    const others = permits.filter((p) => p.type === 'other');

    const data = [
      formatPermit({
        label: generateLabel(iva?.title ?? null, 'Connection Permit', country ?? '', 'IVA'),
        status: iva?.status,
        date: iva?.date,
      }),
      formatPermit({
        label: generateLabel(dia?.title ?? null, 'Environmental permit', country ?? '', 'DIA'),
        status: dia?.status,
        date: dia?.date,
      }),
      formatPermit({
        label: generateLabel(aap?.title ?? null, 'Previous permit', country ?? '', 'AAP'),
        status: aap?.status,
        date: aap?.date,
      }),
      formatPermit({
        label: generateLabel(aac?.title ?? null, 'Construction permit', country ?? '', 'AAC'),
        status: aac?.status,
        date: aac?.date,
      }),
      ...others.map((permit) =>
        formatPermit({
          label: permit.title ?? t('Other permit'),
          status: permit.status,
          date: permit.date,
        })
      ),
    ];

    const filtered = isEditable
      ? data
      : data.filter((f) => f.value !== null && f.value !== undefined && f.value !== '');

    return {
      permitFields: filtered,
      comments: normalizedProject.permitsComments ?? null,
    };
  }, [permits, country, t, normalizedProject.permitsComments, formatPermit, isEditable]);

  if (!isEditable && permitFields.length === 0 && !comments) return null;

  return (
    <div className="rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
      <div className="mb-6 flex items-center">
        <div className="flex grow items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="todo-list" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('Permitting')}
          </Typography>
        </div>
        {isEditable && (
          <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
            {t('Edit')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1">
        {permitFields.map(({ label, value }) => (
          <div key={label} className="flex flex-row items-start gap-x-4">
            <Typography key={label} as="p" size="body-2" weight="semibold" className="mb-1 w-1/2" relaxed>
              {label}:{' '}
            </Typography>
            <Typography
              as="p"
              size="body-2"
              weight="normal"
              className="w-2/3 text-base font-normal whitespace-pre-wrap text-neutral-900"
            >
              {value ?? (isEditable ? '—' : null)}
            </Typography>
          </div>
        ))}
      </div>

      {(comments || isEditable) && (
        <div className="mt-2">
          <Typography as="p" size="body-2" weight="semibold" relaxed>
            {t('Comments')}:{' '}
            <span className="ml-3 text-base font-normal whitespace-pre-wrap text-neutral-900">
              {comments ?? (isEditable ? '—' : null)}
            </span>
          </Typography>
        </div>
      )}
    </div>
  );
}
